export interface FilterLists {
  banned: readonly string[];
  bannedPlain: readonly string[];
  allowed: readonly string[];
}

export interface FilterResult {
  ok: boolean;
  matches: { start: number; end: number }[];
}

const INVISIBLE = /[\u200b-\u200f\u2060\ufeff\u00ad]/g;
const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '@': 'a', $: 's' };

export function stripDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');
}

interface Token {
  word: string;
  plain: string;
  start: number;
  end: number;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  const re = /[\p{L}\p{N}@$]+/gu;
  for (const m of text.matchAll(re)) {
    const raw = m[0].toLowerCase();
    const word = [...raw].map((c) => LEET[c] ?? c).join('');
    tokens.push({ word, plain: stripDiacritics(word), start: m.index, end: m.index + m[0].length });
  }
  return tokens;
}

function phraseWords(phrase: string): string[] {
  return phrase.toLowerCase().split(/\s+/).filter(Boolean);
}

export function createTextFilter(lists: FilterLists) {
  const banned = lists.banned.map(phraseWords);
  const bannedPlain = lists.bannedPlain.map((p) => phraseWords(stripDiacritics(p)));
  const allowed = lists.allowed.map((p) => phraseWords(stripDiacritics(p)));

  const matchAt = (tokens: Token[], i: number, words: string[], plain: boolean): boolean => {
    if (i + words.length > tokens.length) return false;
    for (let k = 0; k < words.length; k++) {
      const t = tokens[i + k] as Token;
      if ((plain ? t.plain : t.word) !== words[k]) return false;
    }
    return true;
  };

  return function check(input: string): FilterResult {
    const text = input.replace(INVISIBLE, '');
    const tokens = tokenize(text);
    const shielded = new Array<boolean>(tokens.length).fill(false);
    for (let i = 0; i < tokens.length; i++) {
      for (const words of allowed) {
        if (matchAt(tokens, i, words, true)) shielded.fill(true, i, i + words.length);
      }
    }
    const matches: FilterResult['matches'] = [];
    for (let i = 0; i < tokens.length; i++) {
      const tryList = (list: string[][], plain: boolean): boolean => {
        for (const words of list) {
          if (!matchAt(tokens, i, words, plain)) continue;
          const span = tokens.slice(i, i + words.length);
          if (span.every((_, k) => shielded[i + k])) continue;
          const first = span[0] as Token;
          const last = span[span.length - 1] as Token;
          matches.push({ start: first.start, end: last.end });
          return true;
        }
        return false;
      };
      // Unaccented forms only count when the typed word itself has no accents, so "lớn" is not read as its stripped twin.
      const typedPlain = (tokens[i] as Token).word === (tokens[i] as Token).plain;
      if (!tryList(banned, false) && typedPlain) tryList(bannedPlain, true);
    }
    return { ok: matches.length === 0, matches };
  };
}

export type TextFilter = ReturnType<typeof createTextFilter>;

/**
 * The word lists load after the splash so they stay off the critical path; until then every
 * text passes, which only matters for input typed in the first moments after load.
 */
export function createLazyTextFilter(lists: Promise<FilterLists>): TextFilter {
  let check: TextFilter | null = null;
  void lists.then((l) => {
    check = createTextFilter(l);
  });
  return (input) => (check ? check(input) : { ok: true, matches: [] });
}
